import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { fmtDate, TYPE_COLOR, cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users, Search, Building2, Sun, Moon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";

export default function PublicEvents() {
  const [q, setQ]           = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { theme, toggleTheme } = useAuth();

  const upcoming = useFetch(() => api.publicFeed({ limit: 50 }), []);
  const recent   = useFetch(() => api.recentFeed(), []);

  const events = upcoming.data?.events || [];
  const recentEvs = recent.data?.events || [];

  const filtered = events.filter(ev =>
    (!q || [ev.title,ev.venue,ev.eventType].join(" ").toLowerCase().includes(q.toLowerCase())) &&
    (typeFilter === "all" || ev.eventType === typeFilter)
  );

  const types = [...new Set(events.map(e => e.eventType).filter(Boolean))];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border/50">
        <div className="orb bg-primary/20 absolute -top-20 -left-20 w-80 h-80 pointer-events-none"/>
        <div className="orb bg-cyan-400/15 absolute top-0 right-0 w-96 h-96 pointer-events-none" style={{animationDelay:"8s"}}/>
        <nav className="relative flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center"><Building2 className="h-4 w-4 text-white"/></div>
            <span className="font-bold text-sm">BMSIT&M Events</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg glass text-muted-foreground hover:text-foreground">
              {theme==="dark"?<Sun className="h-4 w-4"/>:<Moon className="h-4 w-4"/>}
            </button>
            <Link to="/login"><button className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">Sign in</button></Link>
          </div>
        </nav>
        <div className="relative px-6 py-16 text-center max-w-2xl mx-auto">
          <motion.h1 initial={{opacity:0,y:-12}} animate={{opacity:1,y:0}} className="text-4xl sm:text-5xl font-extrabold tracking-tighter mb-3">
            Events at BMSIT&M
          </motion.h1>
          <motion.p initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:0.1}} className="text-muted-foreground text-lg mb-6">
            Workshops, seminars, hackathons, placements and more — all in one place.
          </motion.p>
          <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:0.2}} className="relative max-w-md mx-auto">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
            <Input className="pl-10 h-11 glass" placeholder="Search events…" value={q} onChange={e=>setQ(e.target.value)}/>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Type filters */}
        {types.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={()=>setTypeFilter("all")} className={cn("px-3 py-1 rounded-full text-xs font-medium transition-all", typeFilter==="all"?"bg-primary text-primary-foreground":"glass text-muted-foreground hover:text-foreground")}>All</button>
            {types.map(t=>(
              <button key={t} onClick={()=>setTypeFilter(t)} className={cn("px-3 py-1 rounded-full text-xs font-medium transition-all", typeFilter===t?"bg-primary text-primary-foreground":"glass text-muted-foreground hover:text-foreground capitalize")}>
                {t.replace(/_/g," ")}
              </button>
            ))}
          </div>
        )}

        {/* Upcoming */}
        <div className="mb-10">
          <div className="overline mb-4">Upcoming events {filtered.length > 0 && `— ${filtered.length}`}</div>
          {upcoming.loading ? (
            <div className="grid sm:grid-cols-2 gap-4">{[...Array(4)].map((_,i)=><div key={i} className="h-52 rounded-2xl shimmer"/>)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">No events found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((ev, i) => <EventCard key={ev.id} ev={ev} delay={i*0.04}/>)}
            </div>
          )}
        </div>

        {/* Recent */}
        {recentEvs.length > 0 && (
          <div>
            <div className="overline mb-4">Recent events</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {recentEvs.map((ev, i) => <EventCard key={ev.id} ev={ev} delay={i*0.04} compact/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EventCard({ ev, delay = 0, compact = false }) {
  const typeClass = TYPE_COLOR[ev.eventType] || TYPE_COLOR.other;
  return (
    <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay}}>
      <Link to={`/events/${ev.id}`} className="block glass rounded-2xl overflow-hidden hover:shadow-xl card-tap transition-all duration-200 group">
        {/* Banner */}
        <div className={cn("relative bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center font-extrabold text-primary/20 tracking-tighter", compact ? "h-24 text-4xl" : "h-36 text-6xl")}>
          {ev.bannerUrl ? (
            <img src={ev.bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover"/>
          ) : (
            (ev.title||"").substring(0,2).toUpperCase()
          )}
          <div className="absolute top-2 left-2">
            <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full capitalize", typeClass)}>{ev.eventType?.replace(/_/g," ")}</span>
          </div>
          {ev.regOpen && (
            <div className="absolute top-2 right-2"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500 text-white">OPEN</span></div>
          )}
        </div>
        <div className="p-4">
          <h3 className={cn("font-bold tracking-tight group-hover:text-primary transition-colors line-clamp-2", compact ? "text-sm" : "text-base")}>{ev.title}</h3>
          {!compact && ev.speakerName && <p className="text-xs text-muted-foreground mt-1">{ev.speakerName}</p>}
          <div className="flex flex-wrap gap-3 mt-2.5 text-xs text-muted-foreground">
            {ev.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3"/>{fmtDate(ev.date)}</span>}
            {ev.venue && <span className="flex items-center gap-1"><MapPin className="h-3 w-3"/>{ev.venue}</span>}
            {ev.seatsAvailable > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3"/>{ev.seatsAvailable} seats left</span>}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
